import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import {
  type DiagramArtifactPromptDiagramArtifactPromptType,
  type HouseholdChatPromptTemplate,
} from '@/types/schema';

/** The first request for a new diagram. The editor sends it to the chat. */
export interface NewDiagramPrompt {
  message: string;
  withPromptTemplate?: HouseholdChatPromptTemplate;
  referenceImage?: File;
}

/** Page state for Artifact Studio: the composer input and the open editor. */
export interface ArtifactStudioContext {
  householdId: string;

  /** The free-form request in the composer. */
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedTemplate: DiagramArtifactPromptDiagramArtifactPromptType | null;
  setSelectedTemplate: (
    template: DiagramArtifactPromptDiagramArtifactPromptType | null
  ) => void;
  referenceImage: File | null;
  setReferenceImage: (file: File | null) => void;
  /** True while the composer checks the reference image. Creation waits. */
  isValidatingReferenceImage: boolean;
  setIsValidatingReferenceImage: (isValidating: boolean) => void;
  /** The reference image validation error. Empty when the image is valid. */
  referenceImageError: string;
  setReferenceImageError: (error: string) => void;
  /** True when the composer has a request or a template. */
  canCreateArtifact: boolean;
  /** Opens the editor for a new diagram from the composer input, then clears the composer. */
  createArtifact: () => void;

  isEditorOpen: boolean;
  /** The saved artifact in the editor. Null for a new diagram until the backend creates its artifact. */
  editorArtifactID: string | null;
  /** The first request of the new diagram in the editor. Undefined for a saved artifact. */
  newDiagram: NewDiagramPrompt | undefined;
  openArtifact: (artifactID: string) => void;
  /** Points the editor of a new diagram at the artifact that the backend created for it. */
  showCreatedArtifact: (artifactID: string) => void;
  closeEditor: () => void;
}

export const ArtifactStudioContext =
  createContext<ArtifactStudioContext | null>(null);

export const useArtifactStudioContext = () =>
  useGuardedContext(ArtifactStudioContext, 'ArtifactStudioProvider');
